import { AutoComplete, Col, DatePicker, Form, Input, InputNumber, Modal, Row, Select, Spin, message } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import phytosanitaryProductApi from '../../../../apis/PhytosanitaryProductApi';
import treatmentApi from '../../../../apis/TreatmentApi';
import ConstantLabel from '../../../../components/ConstantLabel/ConstantLabel';
import { PhytosanitaryProduct, Treatment } from '../../../../models/Entities';
import { crops, doseUnits } from '../../../../models/Types';
import alertService from '../../../../services/AlertService';
import stringService from '../../../../services/StringService';
import styles from './TreatmentModal.module.scss';

/**
 * Returns the treatment modal to create or update a treatment.
 * @returns the treatment modal.
 */
const TreatmentModal = (props: Props) => {
    const { plotId, treatmentId, onCancel, onSave } = props;

    /*** HOOKS ***/

    const intl = useIntl();
    const [form] = Form.useForm();
    const [treatment, setTreatment] = useState<Treatment>();
    const [loading, setLoading] = useState<'loading' | 'saving'>();
    const [productSuggestions, setProductSuggestions] = useState<PhytosanitaryProduct[]>([]);
    const [loadingSuggestions, setLoadingSuggestions] = useState<boolean>(false);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();

    /*** EFFECTS ***/

    useEffect(() => {
        const init = async () => {
            try {
                if (treatmentId) {
                    setLoading('loading');
                    const treatment = await treatmentApi.get(treatmentId);
                    setTreatment(treatment);
                    form.setFieldsValue(treatment);
                }
            } catch (error) {
                alertService.displayError(error, intl);
            } finally {
                setLoading(undefined);
            }
        };
        init();
    }, [form, intl, treatmentId]);

    /*** METHODS ***/

    const searchProducts = (registerNumber?: string, name?: string) => {
        clearTimeout(debounceTimerRef.current);
        if (!registerNumber && !name) {
            setProductSuggestions([]);
            return;
        }
        debounceTimerRef.current = setTimeout(async () => {
            try {
                setLoadingSuggestions(true);
                const page = await phytosanitaryProductApi.list(0, 10, 'name', true, registerNumber, name);
                setProductSuggestions(page.content);
            } catch {
                setProductSuggestions([]);
            } finally {
                setLoadingSuggestions(false);
            }
        }, 300);
    };

    const onProductSelect = (_: string, option: { product: PhytosanitaryProduct }) => {
        form.setFieldsValue({
            productName: option.product.name,
            productRegisterNumber: option.product.registerNumber
        });
        setProductSuggestions([]);
    };

    const save = async () => {
        try {
            setLoading('saving');
            const values = await form.validateFields();
            let updatedTreatment: Treatment = Object.assign({}, treatment, values, { plotId });
            if (updatedTreatment.id) {
                await treatmentApi.update(updatedTreatment);
            } else {
                await treatmentApi.create(updatedTreatment);
            }
            message.success(intl.formatMessage({ id: 'status.saved' }));
            onSave();
        } catch (error: any) {
            !error.errorFields && alertService.displayError(error, intl);
        } finally {
            setLoading(undefined);
        }
    };

    /*** VISUAL ***/

    const cropOptions = crops.map((crop) => (
        <Select.Option key={crop} value={crop}>
            {intl.formatMessage({ id: `crop.type.${crop}` })}
        </Select.Option>
    ));

    const doseUnitOptions = doseUnits.map((doseUnit) => (
        <Select.Option key={doseUnit} value={doseUnit}>
            <ConstantLabel value={doseUnit} prefix="treatment.doseUnit." />
        </Select.Option>
    ));

    const nameOptions = productSuggestions.map((p) => ({
        value: p.name,
        label: `${p.name} (${p.registerNumber})`,
        product: p
    }));

    const registerOptions = productSuggestions.map((p) => ({
        value: p.registerNumber,
        label: `${p.registerNumber} - ${p.name}`,
        product: p
    }));

    return (
        <Modal
            title={<FormattedMessage id={treatmentId ? 'treatment.modal.update.title' : 'treatment.modal.create.title'} />}
            open={true}
            onCancel={onCancel}
            onOk={save}
            okText={<FormattedMessage id="button.save" tagName="span" />}
            okButtonProps={{ loading: loading === 'saving', size: 'large' }}
            cancelButtonProps={{ disabled: loading === 'saving', size: 'large' }}
            width={800}
        >
            <Spin spinning={loading === 'loading'}>
                <Form form={form} colon={false} layout="vertical">
                    <Row gutter={[28, 0]}>
                        <Col span={24} lg={12}>
                            <Form.Item
                                label={<FormattedMessage id="treatment.startDate" />}
                                name="startDate"
                                rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                            >
                                <DatePicker className={styles.input} size="large" format="DD/MM/YYYY" />
                            </Form.Item>
                        </Col>
                        <Col span={24} lg={12}>
                            <Form.Item label={<FormattedMessage id="treatment.endDate" />} name="endDate">
                                <DatePicker className={styles.input} size="large" format="DD/MM/YYYY" />
                            </Form.Item>
                        </Col>
                        <Col span={24} lg={16}>
                            <Form.Item
                                label={<FormattedMessage id="treatment.productName" />}
                                name="productName"
                                rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                            >
                                <AutoComplete
                                    size="large"
                                    maxLength={250}
                                    options={nameOptions}
                                    onSearch={(value) => searchProducts(undefined, value || undefined)}
                                    onSelect={onProductSelect}
                                    notFoundContent={loadingSuggestions ? <Spin size="small" /> : null}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={24} lg={8}>
                            <Form.Item
                                label={<FormattedMessage id="treatment.productRegisterNumber" />}
                                name="productRegisterNumber"
                                rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                            >
                                <AutoComplete
                                    size="large"
                                    className={styles.input}
                                    options={registerOptions}
                                    onSearch={(value) => searchProducts(value || undefined, undefined)}
                                    onSelect={onProductSelect}
                                    notFoundContent={loadingSuggestions ? <Spin size="small" /> : null}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={24} lg={12}>
                            <Form.Item
                                label={<FormattedMessage id="treatment.dose" />}
                                name="dose"
                                rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                            >
                                <InputNumber size="large" className={styles.input} min={0} />
                            </Form.Item>
                        </Col>
                        <Col span={24} lg={12}>
                            <Form.Item
                                label={<FormattedMessage id="treatment.doseUnit" />}
                                name="doseUnit"
                                rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                            >
                                <Select size="large" placeholder={<FormattedMessage id="treatment.doseUnit" />} allowClear>
                                    {doseUnitOptions}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={24} lg={12}>
                            <Form.Item
                                label={<FormattedMessage id="treatment.crop" />}
                                name="crop"
                                rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                            >
                                <Select
                                    size="large"
                                    showSearch
                                    filterOption={stringService.filterOptions}
                                    placeholder={<FormattedMessage id="treatment.crop" />}
                                    allowClear
                                >
                                    {cropOptions}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={24} lg={12}>
                            <Form.Item label={<FormattedMessage id="treatment.cropVariety" />} name="cropVariety">
                                <Input size="large" maxLength={250} />
                            </Form.Item>
                        </Col>
                        <Col span={24} lg={12}>
                            <Form.Item
                                label={<FormattedMessage id="treatment.parcel" />}
                                name="parcel"
                                rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                            >
                                <Input size="large" maxLength={250} />
                            </Form.Item>
                        </Col>
                        <Col span={24} lg={12}>
                            <Form.Item
                                label={<FormattedMessage id="treatment.area" />}
                                name="area"
                                rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                            >
                                <InputNumber size="large" className={styles.input} min={0} />
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item label={<FormattedMessage id="treatment.technician" />} name="technician">
                                <Input size="large" maxLength={1000} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Spin>
        </Modal>
    );
};
export default TreatmentModal;

interface Props {
    plotId: number;
    treatmentId?: number;
    onCancel: () => void;
    onSave: () => void;
}
